import { styled, Tooltip } from '@mui/material';
import { FC, useState, useEffect } from 'react';

import { useProcessedBannerStore } from '../../../studentApp/hooks';
import {
  bannerStore,
  problemStore,
  needHelpButtonStore,
} from '../../../studentApp/stores';
import { problemJsonMap } from '../../../studentApp/stores/problemJsonMap';
import {
  NEED_HELP,
  studentAppModalStore,
} from '../../../studentApp/stores/studentAppModalStore';
import { POINTS_LABEL_SIZE, POINTS_LABEL_FONT_FAMILY } from '../../constants';
import { buttonStyle } from '../util/constants';

const NEED_HELP_BUTTON_MIN_WIDTH = '104px';

export const NeedHelpButton: FC = () => {
  const setCurrentModal = studentAppModalStore(state => state.setCurrentModal);
  const [
    replacementsPerDay,
    replacementsUsedToday,
    teacherOnline,
    allowReplacementsWhenTeacherOnline,
  ] = bannerStore(state => [
    state.replacementsPerDay,
    state.replacementsUsedToday,
    state.teacherOnline,
    state.allowReplacementsWhenTeacherOnline,
  ]);
  const selectedID = problemStore(state => state.selectedID);
  const themeColor = useProcessedBannerStore().themeColor;
  const contrastingThemeColor = useProcessedBannerStore().contrastingThemeColor;
  const [isHovered, setIsHovered] = useState(false);

  // Get the currently displayed hasHelp state from the store
  const hasHelp = needHelpButtonStore(state => state.currentDisplayedHasHelp);
  const setCurrentDisplayedHasHelp = needHelpButtonStore(
    state => state.setCurrentDisplayedHasHelp
  );

  // Get the current problem's hasHelp property from problemJsonMap
  const currentProblemData = problemJsonMap.get(selectedID);

  // Update the displayed state when the current problem's data loads
  useEffect(() => {
    if (currentProblemData?.hasHelp !== undefined) {
      setCurrentDisplayedHasHelp(currentProblemData.hasHelp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProblemData?.hasHelp]);

  const showNeedHelpModal = (): void => {
    if (replacementsUsedToday >= replacementsPerDay) return;
    // Otherwise, the popup will appear on the use of last replacement
    // because hovered was set to true in order to click the button,
    // then never back to false because modal overlay prevents mouse
    // moving off the button from being detected.
    setIsHovered(false);
    setCurrentModal(NEED_HELP);
  };

  const isReplacementAllowedByTeacher =
    !teacherOnline || allowReplacementsWhenTeacherOnline;
  const replacementsAvailable = replacementsPerDay - replacementsUsedToday;
  const buttonText = hasHelp ? 'Show Steps' : 'Show Answer';
  const countText =
    replacementsAvailable > 0 ? `(${replacementsAvailable})` : '';

  return (
    <div style={{ marginLeft: 'auto', position: 'relative', flexShrink: 0 }}>
      {isReplacementAllowedByTeacher && replacementsPerDay > 0 && (
        <Tooltip
          title={
            hasHelp
              ? 'See steps and replace problem'
              : 'See answer and replace problem'
          }
        >
          <div
            className={`need-help-button ${
              replacementsUsedToday >= replacementsPerDay
                ? 'disabled-need-help'
                : ''
            }`}
            onClick={showNeedHelpModal}
            onTouchStart={() => setIsHovered(true)}
            onMouseEnter={() => setIsHovered(true)}
            style={{
              ...buttonStyle,
              height: '30px',
              width: 'max-content',
              minWidth: NEED_HELP_BUTTON_MIN_WIDTH,
              padding: '5px 8px',
              gap: '6px',
              flexShrink: 0,
              backgroundColor: replacementsAvailable > 0 ? themeColor : 'gray',
              position: 'relative',
            }}
          >
            {hasHelp && (
              <span
                style={{
                  flexShrink: 0,
                  background: contrastingThemeColor,
                  padding: '1px 5px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                New!
              </span>
            )}
            <ButtonText mainText={buttonText} countText={countText} />
          </div>
        </Tooltip>
      )}

      {isReplacementAllowedByTeacher &&
        replacementsPerDay > 0 &&
        replacementsUsedToday >= replacementsPerDay &&
        isHovered && (
          <div
            className="need-help-disabled-popup"
            onMouseLeave={() => setIsHovered(false)}
          >
            You have used all your daily replacements. You get{' '}
            {replacementsPerDay} per day.
          </div>
        )}
    </div>
  );
};

export const ButtonText: FC<{
  mainText: string;
  countText?: string;
}> = ({ mainText, countText }) => {
  return (
    <StyledButtonText>
      <span
        style={{
          textAlign: countText ? 'left' : 'center',
        }}
      >
        {mainText}
      </span>
      {countText ? (
        <span style={{ textAlign: 'right' }}>{countText}</span>
      ) : null}
    </StyledButtonText>
  );
};

const StyledButtonText = styled('span')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  fontSize: POINTS_LABEL_SIZE,
  fontFamily: POINTS_LABEL_FONT_FAMILY,
  fontWeight: 700,
  gap: '4px',
  flexShrink: 0,
  width: 'max-content',
  whiteSpace: 'nowrap',
  lineHeight: '1.2',
}));
